import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { adminGetResource, adminCreateResource, adminUpdateResource, adminUploadPdf, adminUploadImage } from "@/lib/api";
import { toast } from "sonner";
import { FileText, Image as ImgIcon, Check } from "lucide-react";

const empty = {
  slug: "",
  title: "",
  kind: "guide",
  level: "beginner",
  premium: false,
  price: 0,
  pages: 0,
  description: "",
  tags: [],
  image: "",
  pdf_path: "",
  pdf_filename: "",
  published: true,
};

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function ResourceEditor() {
  const { slug } = useParams();
  const editing = Boolean(slug);
  const navigate = useNavigate();
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);
  const [pdfUploading, setPdfUploading] = useState(false);
  const [pdfProgress, setPdfProgress] = useState(0);
  const [imgUploading, setImgUploading] = useState(false);
  const [tagsText, setTagsText] = useState("");

  useEffect(() => {
    if (editing) {
      adminGetResource(slug).then((r) => {
        setForm(r);
        setTagsText((r.tags || []).join(", "));
      }).catch(() => toast.error("Not found"));
    }
  }, [slug, editing]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handlePdf = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPdfUploading(true); setPdfProgress(0);
    try {
      const res = await adminUploadPdf(file, (p) => {
        if (p.total) setPdfProgress(Math.round((p.loaded / p.total) * 100));
      });
      update("pdf_path", res.path);
      update("pdf_filename", res.original_filename);
      toast.success("PDF uploaded");
    } catch (ex) {
      toast.error(ex.response?.data?.detail || "PDF upload failed");
    } finally {
      setPdfUploading(false); setPdfProgress(0);
    }
  };

  const handleImg = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImgUploading(true);
    try {
      const res = await adminUploadImage(file);
      // Store the media URL so <img src> works publicly
      const abs = `${BACKEND}${res.url}`;
      update("image", abs);
      toast.success("Cover uploaded");
    } catch (ex) {
      toast.error(ex.response?.data?.detail || "Image upload failed");
    } finally {
      setImgUploading(false);
    }
  };

  const save = async () => {
    const payload = {
      ...form,
      tags: tagsText.split(",").map((t) => t.trim()).filter(Boolean),
      price: parseFloat(form.price) || 0,
      pages: parseInt(form.pages) || 0,
    };
    setLoading(true);
    try {
      if (editing) {
        await adminUpdateResource(slug, payload);
        toast.success("Saved");
      } else {
        const created = await adminCreateResource(payload);
        toast.success("Created");
        navigate(`/admin/resources/edit/${created.slug}`);
        return;
      }
    } catch (ex) {
      if (ex.response?.status !== 401) {
        toast.error(ex.response?.data?.detail || "Save failed");
      }
    } finally { setLoading(false); }
  };

  return (
    <div data-testid="resource-editor">
      <Link to="/admin/resources" className="overline editorial-link">← All resources</Link>
      <div className="flex items-center justify-between flex-wrap gap-4 mt-4 mb-10">
        <h1 className="font-serif-editorial tight-track" style={{ fontSize: "clamp(2rem, 3.5vw, 3rem)", lineHeight: 1 }}>
          {editing ? "Edit resource" : "New resource"}
        </h1>
        <div className="flex gap-4 flex-wrap items-center">
          {editing && form.slug && (
            <a
              href={`/resources/${form.slug}?v=${Date.now()}`}
              target="_blank"
              rel="noopener noreferrer"
              className="overline editorial-link"
              data-testid="view-live-resource"
            >
              View live →
            </a>
          )}
          <button onClick={save} disabled={loading} className="btn-olive" data-testid="save-resource">
            {loading ? "Saving…" : "Save"}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
        <div className="md:col-span-2 space-y-5">
          <F label="Title"><input type="text" value={form.title} onChange={(e) => update("title", e.target.value)} className="editor-input" data-testid="input-title" /></F>
          <F label="Slug (auto from title if blank)"><input type="text" value={form.slug || ""} onChange={(e) => update("slug", e.target.value)} className="editor-input" placeholder="my-resource-slug" data-testid="input-slug" /></F>
          <F label="Description"><textarea value={form.description} onChange={(e) => update("description", e.target.value)} rows={5} className="editor-input" data-testid="input-description" /></F>
          <F label="Tags (comma-separated)"><input type="text" value={tagsText} onChange={(e) => setTagsText(e.target.value)} className="editor-input" placeholder="Beginner, Printable, Free" data-testid="input-tags" /></F>

          <div className="border p-6 mt-8" style={{ borderColor: "rgba(166,154,139,0.5)" }} data-testid="pdf-uploader">
            <div className="overline mb-3 flex items-center gap-2"><FileText size={14}/> PDF file</div>
            {form.pdf_path ? (
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="text-sm font-serif-editorial flex items-center gap-2"><Check size={14} style={{color:"var(--sage)"}}/> {form.pdf_filename}</div>
                  <div className="overline mt-1 opacity-70">{form.pdf_path}</div>
                </div>
                <label className="overline editorial-link cursor-pointer">
                  Replace
                  <input type="file" accept="application/pdf" onChange={handlePdf} style={{ display: "none" }} data-testid="pdf-input-replace" />
                </label>
              </div>
            ) : (
              <label className="btn-outline cursor-pointer inline-block">
                <input type="file" accept="application/pdf" onChange={handlePdf} style={{ display: "none" }} data-testid="pdf-input" />
                {pdfUploading ? `Uploading ${pdfProgress}%…` : "Upload PDF"}
              </label>
            )}
            {pdfUploading && (
              <div className="mt-4" style={{ height: 3, background: "rgba(166,154,139,0.3)" }}>
                <div style={{ height: 3, background: "var(--olive)", width: `${pdfProgress}%`, transition: "width 200ms" }} />
              </div>
            )}
            <div className="text-xs mt-3 opacity-70">PDF up to 50 MB. Stored securely — customers get a signed download link via email.</div>
          </div>

          <div className="border p-6" style={{ borderColor: "rgba(166,154,139,0.5)" }} data-testid="image-uploader">
            <div className="overline mb-3 flex items-center gap-2"><ImgIcon size={14}/> Cover image</div>
            {form.image ? (
              <div className="flex items-start gap-4">
                <img src={form.image} alt="cover" className="w-24 h-24 object-cover" />
                <div className="flex-1">
                  <div className="text-xs opacity-70 break-all">{form.image}</div>
                  <label className="overline editorial-link cursor-pointer mt-2 inline-block">
                    Replace
                    <input type="file" accept="image/*" onChange={handleImg} style={{ display: "none" }} data-testid="img-input-replace" />
                  </label>
                  <button onClick={() => update("image", "")} className="overline editorial-link ml-6" style={{ color: "#8a3a3a" }}>Remove</button>
                </div>
              </div>
            ) : (
              <div className="flex flex-col gap-2">
                <label className="btn-outline cursor-pointer inline-block w-fit">
                  <input type="file" accept="image/*" onChange={handleImg} style={{ display: "none" }} data-testid="img-input" />
                  {imgUploading ? "Uploading…" : "Upload cover"}
                </label>
                <div className="text-xs opacity-70">Or paste an existing image URL below.</div>
                <input type="text" value={form.image} onChange={(e) => update("image", e.target.value)} className="editor-input mt-1" placeholder="https://…" data-testid="input-image-url" />
              </div>
            )}
          </div>
        </div>

        <div className="md:col-span-1 space-y-5">
          <F label="Free or Premium">
            <div className="flex gap-4">
              <button onClick={() => update("premium", false)} className="overline editorial-link" style={{ opacity: !form.premium ? 1 : 0.5 }} data-testid="toggle-free">Free</button>
              <button onClick={() => update("premium", true)} className="overline editorial-link" style={{ opacity: form.premium ? 1 : 0.5 }} data-testid="toggle-premium">Premium</button>
            </div>
          </F>
          {form.premium && (
            <F label="Price (USD)"><input type="number" min="0" step="1" value={form.price} onChange={(e) => update("price", e.target.value)} className="editor-input" data-testid="input-price" /></F>
          )}
          <F label="Pages"><input type="number" value={form.pages} onChange={(e) => update("pages", e.target.value)} className="editor-input" data-testid="input-pages" /></F>
          <F label="Kind">
            <select value={form.kind} onChange={(e) => update("kind", e.target.value)} className="editor-input" data-testid="input-kind">
              {["guide", "checklist", "worksheet", "protocol", "meal-plan", "workbook", "one-pager", "template"].map((k) => <option key={k} value={k}>{k}</option>)}
            </select>
          </F>
          <F label="Level">
            <select value={form.level} onChange={(e) => update("level", e.target.value)} className="editor-input" data-testid="input-level">
              {["beginner", "intermediate", "advanced"].map((l) => <option key={l} value={l}>{l}</option>)}
            </select>
          </F>
          <label className="flex items-center gap-3 text-sm">
            <input type="checkbox" checked={form.published} onChange={(e) => update("published", e.target.checked)} data-testid="input-published" />
            Published
          </label>
        </div>
      </div>

      <style>{`
        .editor-input { width: 100%; padding: 0.65rem 0.9rem; border: 1px solid rgba(79,87,70,0.35); background: transparent; color: var(--olive); font-size: 0.95rem; outline: none; }
        .editor-input:focus { border-color: var(--olive); }
      `}</style>
    </div>
  );
}

function F({ label, children }) {
  return <div><div className="overline mb-2">{label}</div>{children}</div>;
}
